import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UserCheck, Ban } from "lucide-react";

import { contactRows, initialsOf } from "@/lib/v3People";
import { useCore } from "../useCore";
import { requiredCollection } from "../requiredCollection";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable, PrivacyNote } from "../components/kit";
import { V3Title, V3Actions, V3_PRIMARY } from "../components/v3";
import { SearchField, ResultTable, ResultRow, V3_GHOST } from "../components/v3app";
import { SegmentTabs } from "../components/v3people";

const useConvs = () => useCore("/member/conversations", (d) => requiredCollection(d, "conversations"));

// M10: incoming, sent and confirmed contacts as separate rows; circle invitations are not in the Core yet (C.6).
export default function Contacts() {
  const res = useConvs();
  const [q, setQ] = useState("");
  const rows = res.data ? contactRows(res.data, q) : [];
  const incoming = rows.find((r) => r.kind === "incoming");
  return (
    <div data-m="M10" data-testid="contacts-page">
      <V3Title title="Kontaktanfragen und Einladungen" intro="Behalte eingehende, gesendete und bestätigte Kontakte getrennt im Blick." />
      <SearchField id="contacts-q" label="Kontakte durchsuchen" placeholder="Name, Thema oder Vorgang suchen …" value={q} onChange={setQ} testid="contacts-search" />
      <div className="mt-[17px]">
        {res.error ? <ConnectionError onRetry={res.reload} /> : !res.data ? <Loading /> : (
          <ResultTable testid="contacts-table" label="Kontakte und Anfragen">
            {rows.map((r) => <ResultRow key={r.id} initial={r.title[0]} title={r.title} context={r.context} to={r.to} testid={`contact-row-${r.id}`} />)}
            <ResultRow initial="E" title="Einladung" context="Kreis-Einladungen · Noch nicht verfügbar" to="/kreise/einladungen" testid="contact-row-invitations" />
          </ResultTable>
        )}
        {res.data && !rows.length && <p className="mt-[11px] text-[14px] text-v3-muted" data-testid="contacts-empty">{q ? "Keine Kontakte passen zu deiner Suche." : "Noch keine Anfragen und keine bestätigten Kontakte."}</p>}
      </div>
      <V3Actions className="mt-[21px]">
        {incoming ? <Link to={incoming.to} data-testid="contacts-open-incoming" className={V3_PRIMARY}>Anfrage ansehen</Link>
          : <Link to="/menschen" data-testid="contacts-discover" className={V3_PRIMARY}>Menschen entdecken</Link>}
      </V3Actions>
      <div className="mt-[16px] flex flex-wrap gap-2">
        <Link to="/kontakte/bestaetigt" data-testid="contacts-confirmed-link" className={V3_GHOST}><UserCheck className="h-4 w-4" aria-hidden="true" />Bestätigte Kontakte</Link>
        <Link to="/sicherheit/blockiert" data-testid="contacts-blocked-link" className={V3_GHOST}><Ban className="h-4 w-4" aria-hidden="true" />Blockierte Kontakte</Link>
      </div>
      <div className="mt-[16px]"><PrivacyNote>Ein Kontakt gibt weder deinen Access Pass noch geschlossene Kreise frei. Nachrichten sind erst nach bestätigtem Kontakt möglich.</PrivacyNote></div>
    </div>
  );
}

// M56: confirmed contacts; "Kontakt auswählen" opens the chosen profile. Removing a contact needs DELETE /member/contacts/{id} (C.3).
export function ConfirmedContacts() {
  const res = useConvs();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [pick, setPick] = useState("");
  const rows = res.data ? contactRows(res.data, q).filter((r) => r.kind === "accepted") : [];
  const chosen = rows.find((r) => r.id === pick);
  return (
    <div data-m="M56" data-testid="contacts-confirmed">
      <V3Title title="Meine bestätigten Kontakte" intro="Dies sind deine bestätigten Kontakte." />
      <SearchField id="contacts-confirmed-q" label="Kontakte durchsuchen" placeholder="Name, Thema oder Vorgang suchen …" value={q} onChange={setQ} testid="contacts-confirmed-search" />
      <SegmentTabs label="Kontaktbereich" testid="contacts-segment" value="all" items={[["all", "Alle Kontakte"], ["requests", "Anfragen", "/kontakte"]]} />
      <div className="mt-[8px]">
        {res.error ? <ConnectionError onRetry={res.reload} /> : !res.data ? <Loading /> : rows.length ? (
          <ResultTable testid="contacts-confirmed-table" label="Bestätigte Kontakte">
            {rows.map((r) => <ResultRow key={r.id} initial={initialsOf(r.other.name)} title={r.name} context={`${r.other.city || "Ort nicht angegeben"} · Nachricht schreiben`}
              onOpen={() => setPick(pick === r.id ? "" : r.id)} expanded={pick === r.id} action={pick === r.id ? "Ausgewählt" : "Auswählen"} testid={`contact-confirmed-${r.id}`}>
              {pick === r.id && <div className="mt-[8px] flex flex-wrap gap-2 rounded-[10px] border border-v3-line bg-white px-[16px] py-[12px]" data-testid={`contact-confirmed-${r.id}-panel`}>
                <Link to={r.to} className={V3_GHOST} data-testid={`contact-confirmed-${r.id}-message`}>Nachricht schreiben</Link>
                {r.other.id && <Link to={`/kontakte/blockieren/${encodeURIComponent(r.other.id)}`} className={V3_GHOST}>Blockieren</Link>}
              </div>}
            </ResultRow>)}
          </ResultTable>
        ) : <p className="mt-[11px] text-[14px] text-v3-muted" data-testid="contacts-confirmed-empty">{q ? "Keine Kontakte passen zu deiner Suche." : "Noch keine bestätigten Kontakte."}</p>}
      </div>
      <div className="mt-[14px]"><Unavailable testid="contacts-remove-unavailable">Einen Kontakt entfernen kann der gemeinsame Core noch nicht. Blockieren ist möglich und beendet den Kontakt.</Unavailable></div>
      <V3Actions className="mt-[21px]">
        <button type="button" disabled={!chosen || !chosen.other.id} aria-describedby={chosen && chosen.other.id ? undefined : "contacts-confirmed-open-hint"} onClick={() => navigate(`/menschen/${encodeURIComponent(chosen.other.id)}`)} data-testid="contacts-confirmed-open" className={V3_PRIMARY}>Kontakt auswählen</button>
        {!(chosen && chosen.other.id) && <span id="contacts-confirmed-open-hint" className="sr-only">{chosen ? "Für diesen Kontakt ist kein Profil verfügbar." : "Wähle zuerst einen Kontakt in der Liste aus."}</span>}
      </V3Actions>
    </div>
  );
}
